/**
 * Kanban Board Component for Task Force tickets
 * Uses @dnd-kit for drag-and-drop functionality
 */

import { useState, useCallback } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  UserIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { Badge, Avatar } from '@/components/ui'
import type { Ticket } from '@/api/taskforce'

// Types
type ColumnId = 'open' | 'in_progress' | 'review' | 'done'

interface KanbanColumn {
  title: string
  tickets: Ticket[]
}

interface KanbanData {
  columns: Record<ColumnId, KanbanColumn>
}

interface KanbanBoardProps {
  data: KanbanData
  onReorder: (ticketId: number, newStatus: ColumnId, position: number) => Promise<void>
  onTicketClick?: (ticket: Ticket) => void
}

// Priority badge config
const priorityConfig: Record<string, { color: string; label: string }> = {
  urgent: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Urgente' },
  high: { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Alta' },
  medium: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Media' },
  low: { color: 'bg-green-100 text-green-700 border-green-200', label: 'Bassa' },
}

// Column header config
const columnConfig: Record<ColumnId, { title: string; color: string; bgColor: string }> = {
  open: { title: 'Backlog', color: 'border-gray-400', bgColor: 'bg-gray-50' },
  in_progress: { title: 'In Progress', color: 'border-blue-400', bgColor: 'bg-blue-50' },
  review: { title: 'In Review', color: 'border-purple-400', bgColor: 'bg-purple-50' },
  done: { title: 'Completati', color: 'border-green-400', bgColor: 'bg-green-50' },
}

// Kanban Card Component
function KanbanCard({
  ticket,
  isDragging = false,
  onClick,
}: {
  ticket: Ticket
  isDragging?: boolean
  onClick?: () => void
}) {
  const priority = priorityConfig[ticket.priority] || priorityConfig.medium
  const isOverdue = ticket.due_date && new Date(ticket.due_date) < new Date()

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-lg border border-gray-200 p-3 shadow-sm
        hover:shadow-md hover:border-gray-300 transition-all cursor-pointer
        ${isDragging ? 'opacity-50 shadow-lg rotate-2' : ''}
      `}
    >
      {/* Header with ID and Priority */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 font-mono">#{ticket.id}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${priority.color}`}>
          {priority.label}
        </span>
      </div>

      {/* Title */}
      <h4 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
        {ticket.title}
      </h4>

      {/* Footer with metadata */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        {/* Due date */}
        {ticket.due_date && (
          <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
            {isOverdue ? (
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            ) : (
              <CalendarIcon className="h-3.5 w-3.5" />
            )}
            <span>{new Date(ticket.due_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}</span>
          </div>
        )}

        {/* Assignee */}
        {ticket.assigned_to ? (
          <div className="flex items-center gap-1">
            <Avatar
              size="xs"
              name={ticket.assigned_to.full_name}
              src={ticket.assigned_to.avatar || undefined}
            />
          </div>
        ) : (
          <div className="flex items-center gap-1 text-gray-400">
            <UserIcon className="h-3.5 w-3.5" />
            <span>Non assegnato</span>
          </div>
        )}
      </div>
    </div>
  )
}

// Sortable Card Wrapper
function SortableCard({
  ticket,
  onClick,
}: {
  ticket: Ticket
  onClick?: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ticket.id.toString() })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <KanbanCard ticket={ticket} isDragging={isDragging} onClick={onClick} />
    </div>
  )
}

// Kanban Column Component
function KanbanColumnComponent({
  columnId,
  column,
  onTicketClick,
}: {
  columnId: ColumnId
  column: KanbanColumn
  onTicketClick?: (ticket: Ticket) => void
}) {
  const config = columnConfig[columnId]
  const ticketIds = column.tickets.map((t) => t.id.toString())

  return (
    <div className={`flex flex-col min-h-[500px] rounded-lg ${config.bgColor}`}>
      {/* Column Header */}
      <div className={`p-3 border-b-2 ${config.color}`}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">{config.title}</h3>
          <Badge variant="default" size="sm">
            {column.tickets.length}
          </Badge>
        </div>
      </div>

      {/* Cards Container */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto">
        <SortableContext items={ticketIds} strategy={verticalListSortingStrategy}>
          {column.tickets.map((ticket) => (
            <SortableCard
              key={ticket.id}
              ticket={ticket}
              onClick={() => onTicketClick?.(ticket)}
            />
          ))}
        </SortableContext>

        {/* Empty state */}
        {column.tickets.length === 0 && (
          <div className="flex items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-sm text-gray-400">Nessun ticket</p>
          </div>
        )}
      </div>
    </div>
  )
}

// Main Kanban Board Component
export default function KanbanBoard({ data, onReorder, onTicketClick }: KanbanBoardProps) {
  const [activeTicket, setActiveTicket] = useState<Ticket | null>(null)
  const [columns, setColumns] = useState(data.columns)

  // Update columns when data changes
  useState(() => {
    setColumns(data.columns)
  })

  // Sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Find which column contains a ticket
  const findColumn = useCallback((ticketId: string): ColumnId | null => {
    for (const [columnId, column] of Object.entries(columns)) {
      if (column.tickets.some((t) => t.id.toString() === ticketId)) {
        return columnId as ColumnId
      }
    }
    return null
  }, [columns])

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const ticketId = active.id.toString()

    // Find the ticket being dragged
    for (const column of Object.values(columns)) {
      const ticket = column.tickets.find((t) => t.id.toString() === ticketId)
      if (ticket) {
        setActiveTicket(ticket)
        break
      }
    }
  }

  // Handle drag over (moving between columns)
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id.toString()
    const overId = over.id.toString()

    const activeColumn = findColumn(activeId)
    let overColumn = findColumn(overId)

    // If over is a column header, use that column
    if (!overColumn && (overId as ColumnId) in columns) {
      overColumn = overId as ColumnId
    }

    if (!activeColumn || !overColumn || activeColumn === overColumn) return

    // Move ticket to new column
    setColumns((prev) => {
      const activeTickets = [...prev[activeColumn].tickets]
      const overTickets = [...prev[overColumn].tickets]

      const activeIndex = activeTickets.findIndex((t) => t.id.toString() === activeId)
      const [movedTicket] = activeTickets.splice(activeIndex, 1)

      // Find position in new column
      const overIndex = overTickets.findIndex((t) => t.id.toString() === overId)
      const insertIndex = overIndex >= 0 ? overIndex : overTickets.length

      overTickets.splice(insertIndex, 0, movedTicket)

      return {
        ...prev,
        [activeColumn]: { ...prev[activeColumn], tickets: activeTickets },
        [overColumn]: { ...prev[overColumn], tickets: overTickets },
      }
    })
  }

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveTicket(null)

    if (!over) return

    const activeId = active.id.toString()
    const overId = over.id.toString()

    const activeColumn = findColumn(activeId)
    let overColumn = findColumn(overId)

    // If over is a column header
    if (!overColumn && (overId as ColumnId) in columns) {
      overColumn = overId as ColumnId
    }

    if (!activeColumn || !overColumn) return

    // Get new position
    const overTickets = columns[overColumn].tickets
    const position = overTickets.findIndex((t) => t.id.toString() === activeId)

    // Call API to persist the change
    try {
      await onReorder(parseInt(activeId), overColumn, position >= 0 ? position : 0)
    } catch (error) {
      console.error('Error reordering ticket:', error)
      // Revert on error
      setColumns(data.columns)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {(Object.entries(columns) as [ColumnId, KanbanColumn][]).map(([columnId, column]) => (
          <KanbanColumnComponent
            key={columnId}
            columnId={columnId}
            column={column}
            onTicketClick={onTicketClick}
          />
        ))}
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeTicket && (
          <div className="transform rotate-3 shadow-2xl">
            <KanbanCard ticket={activeTicket} isDragging />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
